<template>
  <div class="wyksbm-flow">
    <!-- ==================== 顶部步骤导航 ==================== -->
    <div class="wizard-header">
      <div class="wizard-steps">
        <div
          v-for="(step, idx) in steps"
          :key="idx"
          :class="['wizard-step', { active: stepCompleted(idx), current: idx === currentStep, clickable: canNavigateTo(idx) }]"
          @click="onStepClick(idx)"
        >
          <div class="step-circle-wrap">
            <div class="step-circle">
              <span v-if="stepCompleted(idx)" class="step-check">&#10003;</span>
              <span v-else class="step-num">{{ idx + 1 }}</span>
            </div>
            <div v-if="idx < steps.length - 1" class="step-connector">
              <div class="connector-line"></div>
            </div>
          </div>
          <div class="step-info">
            <span class="step-title">{{ step }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== 内容区 ==================== -->
    <a-spin :spinning="loading">
      <div class="wizard-body">
        <!-- ==================== Step 0: 报名须知 ==================== -->
        <div v-show="currentStep === 0" class="wizard-panel fade-in">
          <div class="panel-header">
            <div class="panel-icon">&#128203;</div>
            <div>
              <h2 class="panel-title">报名须知</h2>
              <p class="panel-desc">请仔细阅读以下内容后开始报名</p>
            </div>
          </div>

          <!-- 报名时间提示 -->
          <div v-if="configData.regStart && configData.regEnd" class="time-banner">
            <span class="time-icon">&#128340;</span>
            <span>报名时间：<strong>{{ configData.regStart }}</strong> 至 <strong>{{ configData.regEnd }}</strong></span>
            <a-tag v-if="regTimeState" :color="regTimeState === '进行中' ? 'green' : regTimeState === '未开始' ? 'blue' : 'default'" class="time-tag">{{ regTimeState }}</a-tag>
          </div>

          <div v-if="configLoadError" class="empty-hero">
            <div class="empty-icon">&#9888;</div>
            <p class="empty-text">报名配置加载失败，请刷新页面重试</p>
          </div>

          <div v-if="!configLoadError" class="notice-card">
            <div class="notice-content" v-html="configData.noticeContent || '暂无报名须知'"></div>
          </div>

          <div v-if="!configLoadError" class="confirm-row">
            <a-checkbox v-model="hasRead" @change="onReadChange">
              我已阅读并知晓上述内容
            </a-checkbox>
          </div>

          <div v-if="!configLoadError" class="panel-footer">
            <a-button
              type="primary"
              size="large"
              class="btn-primary-pill"
              :disabled="!canStartReg"
              @click="startReg"
            >
              开始报名
            </a-button>
          </div>
        </div>

        <!-- ==================== Step 1: 信息填报 ==================== -->
        <div v-show="currentStep === 1" class="wizard-panel fade-in">
          <div class="panel-header">
            <div class="panel-icon">&#128221;</div>
            <div class="panel-title-group">
              <h2 class="panel-title">信息填报</h2>
              <p class="panel-desc" v-if="!alreadyReged">确认个人信息并上传所需材料</p>
              <p class="panel-desc" v-else>查看已提交的报名信息</p>
            </div>
            <!-- 已报名：审核状态靠右显示 -->
            <div v-if="alreadyReged && checkinInfo" class="panel-status">
              <a-tag :color="statusTagColor">{{ statusLabel }}</a-tag>
              <span v-if="reviewAlertDetail" class="panel-status-reason">{{ reviewAlertDetail }}</span>
            </div>
          </div>

          <div v-if="configData.regStart && configData.regEnd" class="time-banner mini">
            <span class="time-icon">&#128340;</span>
            <span>报名时间：{{ configData.regStart }} 至 {{ configData.regEnd }}</span>
          </div>

          <!-- ===== 已报名：只读回显 ===== -->
          <template v-if="alreadyReged">

            <div class="info-card">
              <div class="info-card-title">报名信息</div>
              <div class="info-grid">
                <div class="info-cell"><span class="info-label">姓名</span><span class="info-value">{{ (checkinInfo && checkinInfo.name) || userInfo.name || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">身份证号</span><span class="info-value">{{ (checkinInfo && checkinInfo.id_code) || userInfo.idNo || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">专业</span><span class="info-value">{{ (checkinInfo && checkinInfo.profession_name) || userInfo.proName || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">层次</span><span class="info-value">{{ (checkinInfo && checkinInfo.gradation) || userInfo.learnLevel || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">学号</span><span class="info-value">{{ (checkinInfo && checkinInfo.student_number) || userInfo.studentNo || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">手机号</span><span class="info-value">{{ (checkinInfo && checkinInfo.phone) || userInfo.phone || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">应试语种</span><span class="info-value">{{ (checkinInfo && checkinInfo.exam_language) || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">函授站</span><span class="info-value">{{ (checkinInfo && checkinInfo.learning_center) || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">报名时间</span><span class="info-value">{{ (checkinInfo && checkinInfo.create_time) || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">审核状态</span><span class="info-value"><a-tag :color="statusTagColor">{{ statusLabel }}</a-tag></span></div>
              </div>
            </div>

            <!-- 已上传照片 -->
            <div class="upload-row">
              <div class="upload-card">
                <div class="upload-card-header"><span class="upload-card-title">身份证正面</span></div>
                <div class="upload-zone">
                  <img v-if="checkinInfo && checkinInfo.card_b_url" :src="checkinInfo.card_b_url" class="upload-preview" />
                  <div v-else class="upload-empty"><span class="upload-text">未上传</span></div>
                </div>
              </div>
              <div class="upload-card">
                <div class="upload-card-header"><span class="upload-card-title">人像照片</span></div>
                <div class="upload-zone">
                  <img v-if="checkinInfo && checkinInfo.photo_url" :src="checkinInfo.photo_url" class="upload-preview" />
                  <div v-else class="upload-empty"><span class="upload-text">未上传</span></div>
                </div>
              </div>
            </div>

            <div class="panel-footer">
              <a-button class="btn-back" @click="currentStep = 0">上一步</a-button>
              <a-tooltip v-if="checkinInfo && checkinInfo.status === 5" title="资质审核已通过，不允许撤销报名">
                <a-button type="danger" class="btn-danger-pill" disabled>不可撤销</a-button>
              </a-tooltip>
              <a-button
                v-else
                type="danger"
                class="btn-danger-pill"
                :loading="canceling"
                @click="cancelReg"
              >取消报名</a-button>
            </div>
          </template>

          <!-- ===== 未报名：填报表单 ===== -->
          <template v-else>
            <div class="info-card">
              <div class="info-card-title">个人信息</div>
              <div class="info-grid">
                <div class="info-cell"><span class="info-label">姓名</span><span class="info-value">{{ userInfo.name || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">身份证号</span><span class="info-value">{{ userInfo.idNo || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">专业</span><span class="info-value">{{ userInfo.proName || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">层次</span><span class="info-value">{{ userInfo.learnLevel || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">学号</span><span class="info-value">{{ userInfo.studentNo || '-' }}</span></div>
                <div class="info-cell"><span class="info-label">手机号</span><span class="info-value">{{ userInfo.phone || '-' }}</span></div>
              </div>
            </div>

            <template v-if="regTimeState === '进行中'">
              <!-- 语种选择 -->
              <div class="form-section">
                <div class="section-label">应试语种 <span class="required-dot">*</span></div>
                <a-select
                  v-model="language"
                  placeholder="请选择应试语种"
                  class="select-modern"
                  :getPopupContainer="(trigger) => trigger.parentElement"
                >
                  <a-select-option
                    v-for="l in availableLanguages"
                    :key="l.code || l.value"
                    :value="l.code || l.value"
                  >
                    {{ l.name || l.text || l.label }}
                  </a-select-option>
                </a-select>
              </div>

              <!-- 照片上传 -->
              <div class="upload-row">
                <div class="upload-card">
                  <div class="upload-card-header">
                    <span class="upload-card-title">身份证正面</span>
                    <span class="required-dot">*</span>
                  </div>
                  <input
                    ref="idCardInput"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    @change="onIdCardChange"
                    style="display:none"
                  />
                  <div class="upload-zone" @click="$refs.idCardInput.click()">
                    <img v-if="idCardPreview" :src="idCardPreview" class="upload-preview" />
                    <div v-else class="upload-empty">
                      <div class="upload-icon">&#128247;</div>
                      <span class="upload-text">点击上传</span>
                      <span class="upload-sub">JPG / PNG，不超过 5MB</span>
                    </div>
                  </div>
                  <p v-if="configData.idCardHint" class="upload-hint" v-html="configData.idCardHint"></p>
                </div>

                <div class="upload-card">
                  <div class="upload-card-header">
                    <span class="upload-card-title">人像照片</span>
                    <span class="required-dot">*</span>
                  </div>
                  <input
                    ref="photoInput"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    @change="onPhotoChange"
                    style="display:none"
                  />
                  <div class="upload-zone" @click="$refs.photoInput.click()">
                    <img v-if="photoPreview" :src="photoPreview" class="upload-preview" />
                    <div v-else class="upload-empty">
                      <div class="upload-icon">&#128100;</div>
                      <span class="upload-text">点击上传</span>
                      <span class="upload-sub">JPG / PNG，不超过 5MB</span>
                    </div>
                  </div>
                  <p v-if="configData.photoHint" class="upload-hint" v-html="configData.photoHint"></p>
                </div>
              </div>

              <div class="panel-footer">
                <a-button
                  type="primary"
                  size="large"
                  class="btn-primary-pill"
                  :loading="submitting"
                  :disabled="!canSubmitReg"
                  @click="submitReg"
                >提交报名</a-button>
              </div>
            </template>
            <div v-else class="empty-hero">
              <div class="empty-icon">&#128340;</div>
              <p class="empty-text">当前不在报名时间范围内，无法填报</p>
            </div>
          </template>
        </div>

        <!-- ==================== Step 2: 费用缴纳 ==================== -->
        <div v-show="currentStep === 2" class="wizard-panel fade-in">
          <div class="panel-header">
            <div class="panel-icon">&#128179;</div>
            <div>
              <h2 class="panel-title">费用缴纳</h2>
              <p class="panel-desc">完成缴费后方可打印准考证</p>
            </div>
          </div>

          <div v-if="configData.payRemark" class="remark-box" v-html="configData.payRemark"></div>

          <div v-if="configData.payStart && configData.payEnd" class="time-banner mini">
            <span class="time-icon">&#128340;</span>
            <span>缴费时间：{{ configData.payStart }} 至 {{ configData.payEnd }}</span>
          </div>

          <template v-if="isPayTimeValid">
            <!-- 已缴费 -->
            <template v-if="payStatus === 'paid'">
              <div class="result-card success">
                <div class="result-icon big">&#10003;</div>
                <h3 class="result-title">缴费完成</h3>
                <p class="result-desc">您已成功缴纳考试费用</p>
              </div>
              <a-table
                :columns="payColumns"
                :data-source="payRecords"
                :pagination="false"
                :row-key="(record, index) => index"
                class="pay-table"
              />
            </template>
            <!-- 未缴费 -->
            <template v-else>
              <!-- QR扫码外部缴费：仅展示pay_qr_code HTML，不显示金额/项目 -->
              <template v-if="payMethodType === 'qr'">
                <div v-if="configData.payQrCode" class="qr-section" v-html="configData.payQrCode"></div>
                <div v-else class="empty-hero">
                  <div class="empty-icon">&#128179;</div>
                  <p class="empty-text">缴费通道暂未配置</p>
                </div>
              </template>
              <!-- 线上支付(pay1.4/lianda)：显示金额 + 缴费按钮 -->
              <template v-else-if="payMethodType === 'online'">
                <div class="pay-card">
                  <div class="pay-card-row amount">
                    <span class="pay-label">缴费金额</span>
                    <span class="pay-amount">&yen;{{ configData.payAmount || '--' }}</span>
                  </div>
                </div>
                <div class="panel-footer">
                  <a-button class="btn-back" @click="currentStep = 1">上一步</a-button>
                  <a-button type="primary" size="large" class="btn-primary-pill" @click="goPay">立即缴费</a-button>
                </div>
              </template>
              <!-- 自定义缴费方式：以payMethod作为config key查找HTML片段 -->
              <template v-else-if="payMethodType === 'custom'">
                <div v-if="payCustomHtml" class="qr-section" v-html="payCustomHtml"></div>
                <div v-else class="empty-hero">
                  <div class="empty-icon">&#128179;</div>
                  <p class="empty-text">缴费通道暂未配置</p>
                </div>
              </template>
              <!-- 无缴费方式配置 -->
              <template v-else>
                <div class="empty-hero">
                  <div class="empty-icon">&#128179;</div>
                  <p class="empty-text">缴费方式暂未配置，请联系学校</p>
                </div>
              </template>
            </template>
          </template>
          <template v-else>
            <div class="empty-hero">
              <div class="empty-icon">&#128340;</div>
              <p class="empty-text">{{ payTimeHint }}</p>
            </div>
          </template>
        </div>

        <!-- ==================== Step 3: 准考证打印 ==================== -->
        <div v-show="currentStep === 3" class="wizard-panel fade-in">
          <div class="panel-header">
            <div class="panel-icon">&#128196;</div>
            <div>
              <h2 class="panel-title">准考证打印</h2>
              <p class="panel-desc">下载并打印准考证，考试时须携带</p>
            </div>
          </div>

          <div v-if="configData.printStart && configData.printEnd" class="time-banner mini">
            <span class="time-icon">&#128340;</span>
            <span>打印时间：{{ configData.printStart }} 至 {{ configData.printEnd }}</span>
          </div>

          <template v-if="printInfoError">
            <div class="empty-hero">
              <div class="empty-icon">&#9888;</div>
              <p class="empty-text">{{ printInfoErrorMsg }}</p>
            </div>
          </template>
          <template v-else-if="ticketInfo.cardNumb">
            <div class="exam-info-card">
              <div class="exam-section-title">考试安排</div>
              <div class="exam-grid">
                <div class="exam-cell"><span class="exam-label">考试语种</span><span class="exam-value">{{ ticketInfo.examLg || '-' }}</span></div>
                <div class="exam-cell"><span class="exam-label">考试时间</span><span class="exam-value">{{ ticketInfo.examStartEndTimeCn || '-' }}</span></div>
                <div class="exam-cell"><span class="exam-label">考点名称</span><span class="exam-value">{{ ticketInfo.kaodianName || '-' }}</span></div>
                <div class="exam-cell"><span class="exam-label">考场号</span><span class="exam-value">{{ ticketInfo.roomNo || '-' }}</span></div>
                <div class="exam-cell"><span class="exam-label">座位号</span><span class="exam-value highlight">{{ ticketInfo.seatNo || '-' }}</span></div>
                <div class="exam-cell" v-if="ticketInfo.specialInfo"><span class="exam-label">备注</span><span class="exam-value">{{ ticketInfo.specialInfo }}</span></div>
              </div>
              <div class="exam-cell full-width" v-if="ticketInfo.examAddress">
                <span class="exam-label">考试地址</span>
                <span class="exam-value">{{ ticketInfo.examAddress }}</span>
              </div>
            </div>
            <div v-if="!isPrintTimeValid" class="time-banner mini" style="margin-bottom: 16px">
              <span class="time-icon">&#128340;</span>
              <span>{{ printTimeHint }}</span>
            </div>
            <div class="panel-footer">
              <a-button class="btn-back" @click="currentStep = 2">上一步</a-button>
              <a-button v-if="isPrintTimeValid" type="primary" size="large" class="btn-primary-pill" @click="goPrint">下载/打印准考证</a-button>
            </div>
          </template>
          <template v-else>
            <div class="empty-hero">
              <div class="empty-icon">&#128196;</div>
              <p class="empty-text">暂无准考证信息</p>
            </div>
          </template>
        </div>

        <!-- ==================== Step 4: 成绩查询 ==================== -->
        <div v-show="currentStep === 4" class="wizard-panel fade-in">
          <div class="panel-header">
            <div class="panel-icon">&#127891;</div>
            <div>
              <h2 class="panel-title">成绩查询</h2>
              <p class="panel-desc">查看学位外语考试成绩</p>
            </div>
          </div>

          <template v-if="scoreData.isPublished">
            <div class="score-hero">
              <div class="score-label">考试成绩</div>
              <div class="score-number">{{ scoreData.examResult || '-' }}</div>
            </div>
            <div class="exam-info-card">
              <div class="exam-grid">
                <div class="exam-cell"><span class="exam-label">准考证号</span><span class="exam-value">{{ scoreData.examineeNumber || '-' }}</span></div>
                <div class="exam-cell"><span class="exam-label">考试时间</span><span class="exam-value">{{ scoreData.examStartTime ? scoreData.examStartTime + ' 至 ' + scoreData.examEndTime : '-' }}</span></div>
                <div class="exam-cell"><span class="exam-label">考试科目</span><span class="exam-value">{{ scoreData.examLanguage || '-' }}</span></div>
                <div class="exam-cell"><span class="exam-label">考试成绩</span><span class="exam-value highlight">{{ scoreData.examResult || '-' }}</span></div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="empty-hero">
              <div class="empty-icon">&#127891;</div>
              <p class="empty-text">成绩尚未公布，请耐心等待</p>
            </div>
          </template>
        </div>
      </div>
    </a-spin>

    <!-- ==================== 准考证须知确认弹窗（内容取学校配置 candidate_rules，富文本） ==================== -->
    <a-modal
      v-model="ticketNoticeVisible"
      title="准考证打印须知"
      :width="1020"
      :maskClosable="false"
      :keyboard="false"
      okText="我已阅读，继续打印"
      cancelText="取消"
      @ok="onTicketNoticeOk"
    >
      <div class="ticket-notice-content" v-html="configData.ticketNotice || '暂无须知内容'"></div>
    </a-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex';

const NOTICE_READ_PREFIX = 'wyksbm_notice';

export default {
  name: 'WyksbmFlow',
  data() {
    return {
      steps: ['报名须知', '信息填报', '费用缴纳', '准考证打印', '成绩查询'],
      currentStep: 0,
      loading: false,
      // Step 0
      hasRead: false,
      configLoadError: false,
      configData: {},
      configRaw: {},
      // Step 1
      language: '',
      langList: [],
      idCardBase64: '',
      idCardPreview: '',
      photoBase64: '',
      photoPreview: '',
      submitting: false,
      canceling: false,
      alreadyReged: false,
      studentStatus: '',
      checkinInfo: null,
      // Step 2
      payStatus: '',
      payRecords: [],
      payColumns: [
        { title: '缴费时间', dataIndex: 'order_date', key: 'order_date' },
        { title: '金额', dataIndex: 'amount', key: 'amount' },
        { title: '支付方式', dataIndex: 'pay_method', key: 'pay_method' },
        { title: '交易流水号', dataIndex: 'jylsh', key: 'jylsh' },
        { title: '交易号', dataIndex: 'tradeno', key: 'tradeno' },
        { title: '备注', dataIndex: 'remark', key: 'remark' },
      ],
      // Step 3
      ticketInfo: {},
      printInfoError: false,
      printInfoErrorMsg: '',
      // 准考证须知弹窗（内容来自学校配置 candidate_rules）
      ticketNoticeVisible: false,
      ticketNoticeAcked: false,
      // Step 4
      scoreData: {},
    };
  },
  computed: {
    ...mapState(['userInfo']),
    regTimeState() {
      if (!this.configData.regStart || !this.configData.regEnd) return null;
      return this.getTimeState(this.configData.regStart, this.configData.regEnd);
    },
    isPayTimeValid() {
      if (!this.configData.payStart || !this.configData.payEnd) return false;
      return this.getTimeState(this.configData.payStart, this.configData.payEnd) === '进行中';
    },
    payTimeHint() {
      if (!this.configData.payStart || !this.configData.payEnd) return '缴费通道尚未开启';
      const state = this.getTimeState(this.configData.payStart, this.configData.payEnd);
      if (state === '未开始') return '缴费通道尚未开启';
      return '缴费通道已关闭';
    },
    isPrintTimeValid() {
      if (!this.configData.printStart || !this.configData.printEnd) return false;
      return this.getTimeState(this.configData.printStart, this.configData.printEnd) === '进行中';
    },
    printTimeHint() {
      if (!this.configData.printStart || !this.configData.printEnd) return '打印通道尚未开启';
      const state = this.getTimeState(this.configData.printStart, this.configData.printEnd);
      if (state === '未开始') return '打印通道尚未开启';
      return '打印通道已关闭';
    },
    canStartReg() {
      return this.hasRead && !this.alreadyReged && !this.configLoadError;
    },
    canSubmitReg() {
      return this.language && this.idCardBase64 && (this.photoBase64 || this.photoPreview);
    },
    availableLanguages() {
      const isEnglishMajor =
        this.userInfo.proName && this.userInfo.proName.indexOf('英语') !== -1;
      if (!isEnglishMajor) return this.langList;
      return this.langList.filter(function(l) {
        const name = (l.name || l.text || l.label || '').toLowerCase();
        return name !== '英语' && name !== 'english';
      });
    },
    statusTagColor() {
      const s = this.checkinInfo && this.checkinInfo.status;
      if (s === 5) return 'green';
      if (s === 3) return 'blue';
      if (s === 2 || s === 4) return 'red';
      if (s === 1) return 'orange';
      return 'default';
    },
    statusLabel() {
      const s = this.checkinInfo && this.checkinInfo.status;
      const map = { 0: '未提交', 1: '未审核', 2: '照片驳回', 3: '照片通过', 4: '资质驳回', 5: '资质通过', 9: '识别失败' };
      return (s !== null && s !== undefined) ? (map[s] || '未知') : '-';
    },
    // 缴费方式类型: 'qr'=扫码外部缴费, 'online'=线上支付, 'custom'=自定义HTML
    payMethodType() {
      var method = this.configData.payMethod || '';
      if (method === 'pay_qr_code') return 'qr';
      if (method === 'pay1.4' || method === 'lianda') return 'online';
      if (method) return 'custom';
      return '';
    },
    // 自定义缴费方式HTML（method非pay_qr_code/pay1.4/lianda时，作为config key查找）
    payCustomHtml() {
      if (this.payMethodType !== 'custom') return '';
      var method = this.configData.payMethod || '';
      return this.configRaw[method] || '';
    },
    /** 审核驳回/失败原因（照片驳回/资质驳回/识别失败时显示） */
    reviewAlertDetail() {
      const info = this.checkinInfo;
      if (!info) return '';
      const s = info.status;
      // 照片驳回(2) / 资质驳回(4)：显示审核原因
      if (s === 2 || s === 4) {
        return info.audit_reasons || '';
      }
      // 识别失败(9)：显示对比失败原因
      if (s === 9) {
        return info.photo_check_failure_reason || '';
      }
      return '';
    },
  },
  created() {
    const readKey = this.getNoticeReadKey();
    if (readKey) {
      this.hasRead = localStorage.getItem(readKey) === 'true';
    }
    if (this.userInfo && this.userInfo.avatar) {
      this.photoPreview = this.userInfo.avatar;
    }
    this.fetchConfig();
  },
  watch: {
    currentStep(val) {
      if (val === 1) this.fetchCheckinInfo(true);
      if (val === 2) this.fetchPayList();
      if (val === 3) this.fetchPrintInfo();
      if (val === 4) this.fetchExamResult();
    },
  },
  methods: {
    getTimeState(startStr, endStr) {
      const now = new Date();
      const start = new Date(startStr);
      const end = new Date(endStr);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
      if (now < start) return '未开始';
      if (now > end) return '已结束';
      return '进行中';
    },
    parseTimeRange(str) {
      if (!str || typeof str !== 'string') return null;
      var match = str.match(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})\s*-\s*(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})$/);
      if (!match) return null;
      var start = new Date(match[1]);
      var end = new Date(match[2]);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
      return { start: match[1], end: match[2] };
    },
    parseHintField(str) {
      if (!str) return {};
      if (typeof str === 'object') return str;
      try {
        return JSON.parse(str);
      } catch (e) {
        return { v: str };
      }
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    async onIdCardChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      try {
        this.idCardBase64 = await this.fileToBase64(file);
        this.idCardPreview = this.idCardBase64;
      } catch (err) {
        this.$message.error('身份证照片读取失败');
      }
    },
    async onPhotoChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      try {
        this.photoBase64 = await this.fileToBase64(file);
        this.photoPreview = this.photoBase64;
      } catch (err) {
        this.$message.error('人像照片读取失败');
      }
    },
    getNoticeReadKey() {
      if (!this.userInfo || !this.userInfo.id) return null;
      const year = new Date().getFullYear();
      return `${NOTICE_READ_PREFIX}_${this.userInfo.id}_${year}`;
    },
    onReadChange(checked) {
      var key = this.getNoticeReadKey();
      // localStorage 有记录时不允许取消勾选（之前已完成开始报名流程）
      if (key && localStorage.getItem(key) === 'true' && !checked) {
        this.$message.info('您已完成报名须知确认，无需重复阅读');
        this.$nextTick(() => { this.hasRead = true; });
      }
    },
    stepCompleted(idx) {
      // 基于业务状态判定步骤是否完成，不随 currentStep 回退而丢失
      if (idx === 0) return this.alreadyReged || this.hasRead;
      if (idx === 1) return this.alreadyReged;
      if (idx === 2) return this.payStatus === 'paid';
      if (idx === 3) return !!(this.ticketInfo && this.ticketInfo.cardNumb);
      if (idx === 4) return !!(this.checkinInfo && this.checkinInfo.exam_result) || !!(this.scoreData && this.scoreData.examResult);
      return false;
    },
    canNavigateTo(idx) {
      if (idx >= 1 && this.configLoadError) return false;
      if (idx >= 1 && !this.hasRead && !this.alreadyReged) return false;
      if (idx >= 2 && this.studentStatus !== '5') return false;
      return true;
    },
    async fetchConfig() {
      this.loading = true;
      try {
        const res = await this.axios.get('/biz/degree/signup/config');
        if (res.success || res.code === 200) {
          const data = res.result || res.data || {};
          this.configRaw = data;
          const repairRange = this.parseTimeRange(data.repair_time);
          const payRange = this.parseTimeRange(data.pay_time_zone);
          const printRange = this.parseTimeRange(data.print_time);
          // 解析 Whether_payment: "是否缴费,缴费方式,缴费金额"
          var wpParts = (data.Whether_payment || '').split(',');
          var payIsPaid = wpParts[0] === '1';
          var payMethod = (wpParts[1] || '').trim();
          var payAmount = wpParts[2] || '';
          this.configData = {
            noticeContent: data.test_explanation || '',
            regStart: repairRange ? repairRange.start : '',
            regEnd: repairRange ? repairRange.end : '',
            payStart: payRange ? payRange.start : '',
            payEnd: payRange ? payRange.end : '',
            printStart: printRange ? printRange.start : '',
            printEnd: printRange ? printRange.end : '',
            payIsPaid: payIsPaid,
            payMethod: payMethod,
            payAmount: payAmount,
            payQrCode: data.pay_qr_code || '',
            payRemark: data.pay_remark || '',
            // 准考证须知（学校配置表 candidate_rules，富文本 HTML）
            ticketNotice: data.candidate_rules || '',
            // 提示词优先使用 remark（含HTML），模板 v-html 直接渲染
            idCardHint: data.idCardA_hint_remark || data.idCardA_hint || '',
            photoHint: data.photo_hint_remark || data.photo_hint || '',
          };
          const langStr = data.language || '英语';
          this.langList = langStr.split(',').map(function(s) { return { code: s.trim(), name: s.trim() }; });
          // payStatus 以实际缴费记录为准（fetchPayList 中判定）
          // 学生状态以 /checkinInfo 为准
          this.fetchCheckinInfo();
        }
      } catch (e) {
        console.error('获取报名配置失败', e);
        // 配置加载失败时阻断流程，Step 0 显示明确错误，避免误报“不在报名时间范围”
        this.configLoadError = true;
      } finally {
        this.loading = false;
      }
    },
    onStepClick(idx) {
      if (!this.canNavigateTo(idx)) {
        if (idx >= 1 && this.configLoadError) {
          this.$message.warning('报名配置加载失败，请刷新页面重试');
          return;
        }
        if (idx >= 1 && !this.hasRead && !this.alreadyReged) {
          this.$message.warning('请先阅读报名须知并勾选确认');
          return;
        }
        if (idx >= 2 && this.studentStatus !== '5') {
          this.$message.warning('您的报名信息尚未通过资质审核，审核通过后方可进入');
          return;
        }
      }
      this.currentStep = idx;
    },
    async startReg() {
      if (!this.canStartReg) return;
      if (!this.hasRead) {
        this.$message.warning('请先阅读报名须知并勾选确认');
        return;
      }
      // 1. 先调用日志接口
      try {
        await this.axios.post('/biz/degree/signup/addOperateLog', {
          title: '阅读报名须知',
          content: '已阅读报名须知并开始填报',
          remark: '',
        });
      } catch (e) {
        // 日志写入失败则阻断流程，不进入下一步
        this.$message.error('操作失败，请稍后重试');
        return;
      }
      // 2. 日志成功后记入缓存
      var readKey = this.getNoticeReadKey();
      if (readKey) {
        localStorage.setItem(readKey, 'true');
      }
      // 3. 进入信息填报
      this.currentStep = 1;
    },
    async submitReg() {
      if (!this.language) { this.$message.warning('请选择应试语种'); return; }
      if (!this.idCardBase64) { this.$message.warning('请上传身份证正面照片'); return; }
      if (!this.photoBase64) { this.$message.warning('请上传人像照片'); return; }
      this.submitting = true;
      try {
        const res = await this.axios.post('/biz/degree/signup/checkin', {
          language: this.language,
          photoUrl: this.photoBase64,
          cardBUrl: this.idCardBase64,
        });
        if (res.success || res.code === 200) {
          this.$message.success('报名成功，请等待资质审核通过后进行后续操作');
          this.alreadyReged = true;
          this.studentStatus = '1';
          this.fetchCheckinInfo(true);
        } else {
          this.$message.error(res.message || '报名失败');
        }
      } catch (e) {
        // 拦截器已统一处理错误提示，此处不再重复弹出
      } finally {
        this.submitting = false;
      }
    },
    async cancelReg() {
      const self = this;
      this.$confirm({
        title: '确认取消报名',
        content: '取消报名后需重新填报信息，确定要取消吗？',
        onOk() {
          self.canceling = true;
          self.axios
            .post('/biz/degree/signup/cancelCheckin')
            .then((res) => {
              if (res.success || res.code === 200) {
                var result = res.result || {};
                var failedList = result.failed_list || [];
                if (failedList.length > 0) {
                  self.$message.error(failedList[0].reason || '取消失败');
                } else {
                  self.$message.success('已取消报名');
                  self.alreadyReged = false;
                  self.checkinInfo = null;
                  self.studentStatus = '';
                  self.language = '';
                  self.idCardBase64 = '';
                  self.idCardPreview = '';
                  self.photoBase64 = '';
                  self.photoPreview = '';
                  self.currentStep = 0;
                  // 清除本地缓存，重新报名需重新勾选须知
                  var rk = self.getNoticeReadKey();
                  if (rk) localStorage.removeItem(rk);
                }
              } else {
                self.$message.error(res.message || '取消失败');
              }
            })
            .finally(() => { self.canceling = false; });
        },
      });
    },
    async fetchCheckinInfo(skipRedirect) {
      try {
        const res = await this.axios.get('/biz/degree/signup/checkinInfo');
        if (res.success || res.code === 200) {
          this.checkinInfo = res.result || res.data || {};
          const rawStatus = this.checkinInfo.status;
          if (rawStatus !== null && rawStatus !== undefined && rawStatus !== '') {
            // 外部系统返回的 status 可能是数字或字符串，统一归一化为数字，避免模板严格相等（=== 5）判断失效
            const numStatus = Number(rawStatus);
            if (!isNaN(numStatus)) {
              this.checkinInfo.status = numStatus;
            }
            this.studentStatus = String(numStatus);
            // 状态为有效数字且不为 0（未提交）时视为已报名
            if (!isNaN(numStatus) && numStatus !== 0) {
              this.alreadyReged = true;
              // 仅初始加载时自动跳转，用户手动导航时不跳转
              if (!skipRedirect) {
                if (numStatus === 5) {
                  // 先拉取缴费记录再决定落点，避免 payStatus 尚未初始化导致已缴费学生误落在缴费步骤
                  await this.fetchPayList();
                  this.currentStep = this.payStatus === 'paid' ? 3 : 2;
                } else {
                  // 已报名但未审核/审核中/驳回：进入信息填报页查看回显与审核状态
                  this.currentStep = 1;
                }
              }
            }
          }
        }
      } catch (e) { console.error('获取报名信息失败', e); }
    },
    async fetchPayList() {
      this.loading = true;
      try {
        const res = await this.axios.get('/biz/degree/signup/payList');
        if (res.success || res.code === 200) {
          const result = res.result || res.data;
          // result 可能是数组（直接返回列表）或对象（含 records/list 字段）
          if (Array.isArray(result)) {
            this.payRecords = result;
          } else if (result && typeof result === 'object') {
            this.payRecords = result.records || result.list || [];
          } else {
            this.payRecords = [];
          }
          // 以实际缴费记录为准判定是否已缴费
          if (this.payRecords.length > 0) {
            this.payStatus = 'paid';
          } else {
            this.payStatus = 'unpaid';
          }
          if (!this.configData.payAmount && this.payRecords.length > 0) {
            this.configData.payAmount = this.payRecords[0].amount || '';
          }
        }
      } catch (e) { console.error('获取缴费信息失败', e); }
      finally { this.loading = false; }
    },
    async goPay() {
      try {
        const res = await this.axios.get('/biz/degree/signup/payUrl');
        if (res.success || res.code === 200) {
          const data = res.result || res.data || {};
          // student.getOnlinePayUrl 按平台返回不同格式
          if (data.pay_url) {
            // pay1.4 天财：直接打开支付链接
            window.open(data.pay_url, '_blank');
            // 支付窗口打开后弹出确认层询问支付结果
            this.showPayConfirm();
          } else if (data.form_action) {
            // lianda 联大：form_action + form_fields
            var form = document.createElement('form');
            form.action = data.form_action;
            form.method = 'POST';
            form.target = '_blank';
            form.style.display = 'none';
            var fields = data.form_fields || {};
            Object.keys(fields).forEach(function(k) {
              var input = document.createElement('input');
              input.name = k;
              input.value = fields[k];
              form.appendChild(input);
            });
            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);
            // 支付窗口打开后弹出确认层询问支付结果
            this.showPayConfirm();
          } else {
            this.$message.warning('未获取到支付链接');
          }
        } else { this.$message.error(res.message || '获取支付链接失败'); }
      } catch (e) { /* 拦截器已处理 */ }
    },
    /** 支付窗口打开后弹出确认层：确认已支付则刷新缴费数据，未支付则仅关闭弹窗 */
    showPayConfirm() {
      const self = this;
      this.$confirm({
        title: '支付结果确认',
        content: '支付页面已在新窗口打开，完成缴费后请点击"支付成功"刷新缴费状态',
        okText: '支付成功',
        cancelText: '暂未支付',
        onOk() {
          return self.fetchPayList().then(() => {
            if (self.payStatus === 'paid') {
              self.$message.success('缴费成功');
            } else {
              self.$message.warning('暂未查询到缴费记录，如已完成支付请稍后刷新查看');
            }
          });
        },
        onCancel() {
          // 未支付成功：仅关闭弹窗，不做任何动作
        },
      });
    },
    async fetchPrintInfo() {
      this.loading = true;
      this.printInfoError = false;
      try {
        const res = await this.axios.get('/biz/degree/signup/printInfo');
        if (res.success || res.code === 200) {
          const data = res.result || res.data || {};
          this.ticketInfo = {
            schoolName: data.schoolName || '-',
            cardNumb: data.cardNumb || '-',
            stuName: data.stuName || '-',
            stuSex: data.stuSex === '1' ? '男' : data.stuSex === '2' ? '女' : '-',
            examLg: data.examLg || '-',
            examineeClass: data.examineeClass || '-',
            stuNumber: data.stuNumber || '-',
            idCode: data.idCode || '-',
            proName: data.proName || '-',
            learningCenter: data.learningCenter || '-',
            kaodianName: data.kaodianName || '-',
            examAddress: data.examAddress || '-',
            roomNo: data.roomNo || '-',
            seatNo: data.seatNo || '-',
            examStartEndTimeCn: data.examStartEndTimeCn || data.examStartEndTime || '-',
            specialInfo: data.special_info || '',
            notice: data.notice || '',
          };
          this.printInfoError = false;
        } else {
          this.printInfoError = true;
          this.printInfoErrorMsg = res.message || '获取准考证信息失败';
          this.ticketInfo = {};
        }
      } catch (e) {
        this.printInfoError = true;
        this.ticketInfo = {};
        var errMsg = (e && e.data && e.data.message) || (e && e.message) || '';
        this.printInfoErrorMsg = errMsg || '获取准考证信息失败，请稍后重试';
        console.error('获取准考证信息失败', e);
      }
      finally { this.loading = false; }
    },
    async goPrint() {
      // 已配置准考证须知（candidate_rules）且本次会话未确认时，先弹须知，确认后再继续打印
      if (this.configData.ticketNotice && !this.ticketNoticeAcked) {
        this.ticketNoticeVisible = true;
        return;
      }
      await this.doPrint();
    },
    onTicketNoticeOk() {
      this.ticketNoticeAcked = true;
      this.ticketNoticeVisible = false;
      this.doPrint();
    },
    async doPrint() {
      try {
        const res = await this.axios.get('/biz/degree/signup/printUrl');
        if (res.success || res.code === 200) {
          var result = res.result || res.data;
          // result 可能是字符串（直接 URL）或对象 { url: "..." }
          var url = typeof result === 'string' ? result : (result && result.url);
          if (url) { window.open(url, '_blank'); }
          else { this.$message.warning('未获取到准考证下载链接'); }
        } else { this.$message.error(res.message || '获取下载链接失败'); }
      } catch (e) { /* 拦截器已处理 */ }
    },
    async fetchExamResult() {
      this.loading = true;
      try {
        const res = await this.axios.get('/biz/degree/signup/examResult');
        if (res.success || res.code === 200) {
          const data = res.result || res.data || {};
          this.scoreData = {
            isPublished: !!data.exam_result,
            examResult: data.exam_result || '',
            examLanguage: data.exam_language || '',
            examineeNumber: data.examination_number || '',
            houseNumber: data.house_number || '',
            sessionNumb: data.session_numb,
            examStartTime: data.exam_start_time || '',
            examEndTime: data.exam_end_time || '',
            examSite: data.exam_site || '',
            examVenue: data.exam_venue || '',
            examAddress: data.exam_address || '',
          };
        }
      } catch (e) { console.error('获取成绩失败', e); }
      finally { this.loading = false; }
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '~styles/varibles.styl';

.wyksbm-flow
  padding 0

/* ==================== 步骤导航 ==================== */
.wizard-header
  background #fff
  border-radius 8px
  padding 28px 32px 20px
  margin-bottom 20px
  box-shadow 0 1px 6px rgba(0,0,0,0.06)

.wizard-steps
  display flex
  align-items flex-start
  justify-content center

.wizard-step
  display flex
  flex-direction column
  align-items center
  flex 1
  cursor pointer
  position relative

  .step-circle-wrap
    display flex
    align-items center
    width 100%
    position relative

  .step-circle
    width 36px
    height 36px
    border-radius 50%
    background #E8E8E8
    color #999
    display flex
    align-items center
    justify-content center
    font-size 14px
    font-weight 600
    transition all 0.35s ease
    z-index 2
    flex-shrink 0
    margin 0 auto

  .step-connector
    position absolute
    top 50%
    left calc(50% + 22px)
    right calc(-50% + 22px)
    height 3px
    background #E8E8E8
    border-radius 2px
    overflow hidden
    transform translateY(-50%)

    .connector-line
      width 0%
      height 100%
      background $bgColor
      border-radius 2px
      transition width 0.5s ease

  &.active .step-connector .connector-line
    width 100%

  &.active .step-circle
    background $bgColor
    color #fff
    box-shadow 0 3px 12px rgba(44,175,122,0.3)

  &.current .step-circle
    background $bgColor
    color #fff
    box-shadow 0 3px 12px rgba(44,175,122,0.3)
    transform scale(1.1)

  .step-check
    font-size 16px

  .step-info
    margin-top 10px
    text-align center

  .step-title
    font-size 13px
    color #999
    transition all 0.3s
    display block

  &.active .step-title
    color $bgColor

  &.current .step-title
    color #1890ff
    font-weight 700
    font-size 15px

/* ==================== 内容面板 ==================== */
.wizard-body
  background transparent

.wizard-panel
  background #fff
  border-radius 8px
  padding 32px 36px
  box-shadow 0 1px 6px rgba(0,0,0,0.06)

.fade-in
  animation fadeIn 0.35s ease

@css {
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
}

/* Panel Header */
.panel-header
  display flex
  align-items center
  margin-bottom 28px
  padding-bottom 20px
  border-bottom 1px solid #f0f0f0

.panel-icon
  width 48px
  height 48px
  border-radius 12px
  background rgba(44,175,122,0.08)
  display flex
  align-items center
  justify-content center
  font-size 24px
  margin-right 16px
  flex-shrink 0

.panel-title-group
  flex 1

.panel-title
  font-size 20px
  font-weight 600
  color #333
  margin 0

.panel-desc
  font-size 14px
  color #999
  margin-top 4px

.panel-status
  display flex
  flex-direction column
  align-items flex-end
  gap 6px
  margin-left 16px
  flex-shrink 0
  max-width 360px

  .ant-tag
    font-size 14px
    padding 2px 12px
    line-height 24px

.panel-status-reason
  font-size 14px
  font-weight 500
  color #cf1322
  text-align right
  line-height 1.5

/* ==================== 时间横幅 ==================== */
.time-banner
  display flex
  align-items center
  background rgba(31,140,235,0.06)
  border 1px solid rgba(31,140,235,0.15)
  border-radius 6px
  padding 12px 16px
  margin-bottom 20px
  font-size 14px
  color #333
  gap 8px

  &.mini
    background #f8f9fa
    border 1px solid #eee
    font-size 13px
    color #666

  .time-icon
    font-size 16px
    flex-shrink 0

  .time-tag
    margin-left auto

/* ==================== 准考证须知弹窗 ==================== */
.ticket-notice-content
  max-height 55vh
  overflow-y auto
  line-height 1.8
  word-break break-word
.ticket-notice-content img
  max-width 100%

/* ==================== 报名须知 ==================== */
.notice-card
  background #fafbfc
  border 1px solid #eef0f2
  border-left 4px solid $bgColor
  border-radius 0 6px 6px 0
  padding 24px 28px
  min-height 200px
  line-height 1.9
  font-size 15px
  color #444
  margin-bottom 24px

.confirm-row
  padding 16px 0
  border-top 1px solid #f0f0f0

/* ==================== 信息卡片 ==================== */
.info-card
  background #fafbfc
  border 1px solid #eef0f2
  border-radius 8px
  padding 20px 24px
  margin-bottom 24px

.info-card-title
  font-size 15px
  font-weight 600
  color #333
  margin-bottom 16px
  padding-bottom 10px
  border-bottom 1px solid #eef0f2

.info-grid
  display grid
  grid-template-columns 1fr 1fr
  gap 12px 32px

.info-cell
  display flex
  align-items center
  padding 8px 0

.info-label
  width 80px
  font-size 13px
  color #999
  flex-shrink 0

.info-value
  font-size 15px
  color #333
  font-weight 500

/* ==================== 表单区域 ==================== */
.form-section
  margin-bottom 24px

.section-label
  font-size 15px
  font-weight 500
  color #333
  margin-bottom 10px

.required-dot
  color #ff4d4f
  margin-left 2px

.select-modern
  width 320px

/* ==================== 照片上传 ==================== */
.upload-row
  display flex
  align-items flex-start
  gap 24px
  margin-bottom 24px

.upload-card
  flex 1
  background #fafbfc
  border 1px solid #eef0f2
  border-radius 8px
  padding 20px
  transition border-color 0.3s

  &:hover
    border-color $bgColor

.upload-card-header
  display flex
  align-items center
  margin-bottom 12px

.upload-card-title
  font-size 14px
  font-weight 500
  color #333

.upload-zone
  width 100%
  min-height 160px
  border 2px dashed #d9d9d9
  border-radius 8px
  cursor pointer
  display flex
  align-items center
  justify-content center
  overflow hidden
  transition all 0.3s
  background #fff

  &:hover
    border-color $bgColor
    background rgba(44,175,122,0.02)

.upload-empty
  text-align center

.upload-icon
  font-size 32px
  margin-bottom 8px

.upload-text
  display block
  font-size 14px
  color #666

.upload-sub
  display block
  font-size 12px
  color #bbb
  margin-top 4px

.upload-preview
  display block
  width 100%
  height auto
  border-radius 6px

.upload-hint
  margin-top 10px
  font-size 12px
  color #999
  line-height 1.6

.upload-remark
  margin-top 8px
  font-size 13px
  color #555
  line-height 1.7
  background #fafbfc
  border 1px solid #eef0f2
  border-radius 4px
  padding 10px 12px

/* ==================== 缴费 ==================== */
.remark-box
  background #fffbe6
  border 1px solid #ffe58f
  border-radius 6px
  padding 14px 18px
  margin-bottom 16px
  font-size 13px
  line-height 1.7

.pay-card
  background #fafbfc
  border 1px solid #eef0f2
  border-radius 8px
  padding 24px 28px
  margin-bottom 24px

.pay-card-row
  display flex
  align-items center
  justify-content space-between
  padding 12px 0

  &.amount .pay-amount
    font-size 32px
    font-weight 700
    color $bgColor

.pay-card-divider
  height 1px
  background #eef0f2
  margin 4px 0

.pay-label
  font-size 14px
  color #999

.pay-value
  font-size 15px
  color #333
  font-weight 500

.pay-table
  margin-top 20px

.result-card
  text-align center
  padding 32px 0 24px

  &.success .result-icon
    color $bgColor

.result-icon
  width 56px
  height 56px
  border-radius 50%
  background rgba(44,175,122,0.1)
  display inline-flex
  align-items center
  justify-content center
  font-size 28px
  margin-bottom 12px

.result-title
  font-size 20px
  font-weight 600
  color #333
  margin-bottom 6px

.result-desc
  font-size 14px
  color #999

.qr-section
  margin-top 20px
  padding 24px
  border 1px solid #eef0f2
  border-radius 8px
  text-align center
  background #fafbfc

/* ==================== 准考证/成绩 ==================== */
.exam-info-card
  background #fafbfc
  border 1px solid #eef0f2
  border-radius 8px
  padding 24px 28px
  margin-bottom 24px

.exam-grid
  display grid
  grid-template-columns 1fr 1fr
  gap 16px 32px

.exam-section-title
  font-size 14px
  font-weight 700
  color #333
  margin-bottom 12px
  padding-bottom 8px
  border-bottom 1px solid #eef0f2

.exam-cell
  padding 8px 0

  &.wide,
  &.full-width
    grid-column span 2

.exam-label
  display block
  font-size 12px
  color #999
  margin-bottom 4px
  text-transform uppercase
  letter-spacing 0.5px

.exam-value
  font-size 16px
  color #333
  font-weight 500

  &.highlight
    color $bgColor
    font-weight 700
    font-size 18px

/* Score Hero */
.score-hero
  text-align center
  padding 32px 0
  margin-bottom 24px
  background linear-gradient(135deg, rgba(44,175,122,0.06) 0%, rgba(44,175,122,0.02) 100%)
  border-radius 8px

.score-label
  font-size 14px
  color #999
  margin-bottom 8px

.score-number
  font-size 56px
  font-weight 700
  color $bgColor
  line-height 1.1

.score-lang
  font-size 14px
  color #666
  margin-top 8px

/* ==================== 空状态 ==================== */
.empty-hero
  text-align center
  padding 60px 0

.empty-icon
  font-size 48px
  margin-bottom 16px
  opacity 0.5

.empty-text
  font-size 15px
  color #999

/* ==================== 操作栏 & 按钮 ==================== */
.panel-footer
  margin-top 28px
  padding-top 20px
  border-top 1px solid #f0f0f0
  display flex
  align-items center
  gap 12px

.btn-primary-pill
  background $bgColor !important
  border-color $bgColor !important
  border-radius 17px !important
  height 40px !important
  padding 0 32px !important
  font-size 15px !important
  font-weight 500
  box-shadow 0 3px 12px rgba(44,175,122,0.25)
  transition all 0.3s

  &:hover:not([disabled])
    background #25a06e !important
    border-color #25a06e !important
    box-shadow 0 4px 16px rgba(44,175,122,0.35)
    transform translateY(-1px)

  &[disabled]
    opacity 0.5
    cursor not-allowed
    box-shadow none

.btn-danger-pill
  border-radius 17px !important
  height 40px !important
  padding 0 24px !important
  font-size 15px !important

.btn-back
  border-radius 17px !important
  height 40px !important
  padding 0 24px !important
  font-size 15px !important
  color #666 !important
  border-color #d9d9d9 !important

  &:hover
    color $bgColor !important
    border-color $bgColor !important
</style>



// WEBPACK FOOTER //
// src/pages/wyksbm/WyksbmFlow.vue